import React, { useState, useEffect, useMemo, useRef } from "react";

import { useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import FavoritesContextMenu from "../components/FavoritesContextMenu";
import FavoritesSortPill from "../components/FavoritesSortPill";
import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import PlyrVideoPlayer from "../components/PlyrVideoPlayer";
import useFontSize from "../hooks/useFontSize";
import apiService from "../services/api";
import { sortFavorites } from "../utils/favoritesSort";

const TYPE_META = {
  tv: { title: "All TV Channels", key: "liveTv", mediaType: "live-tv" },
  series: { title: "All Favorited Series", key: "series", mediaType: "series" },
  movie: { title: "All Favorited Movies", key: "movies", mediaType: "movie" },
  collection: {
    title: "Saved Collections",
    key: "collections",
    mediaType: "collection",
  },
  cast: { title: "Favorited Cast", key: "people", mediaType: "cast" },
};

const WebPlayerFavoritesGrid = () => {
  useFontSize();
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useParams();
  const meta = TYPE_META[type] || TYPE_META.movie;
  const isLogo = type === "tv";

  const profile =
    location.state?.profile ||
    JSON.parse(sessionStorage.getItem("selectedProfile") || "{}");
  const playlist =
    location.state?.playlist ||
    JSON.parse(localStorage.getItem("selectedPlaylist") || "{}");

  const [items, setItems] = useState(location.state?.items || []);
  const [query, setQuery] = useState("");
  const [sortOption, setSortOption] = useState(
    () =>
      location.state?.sortOption ||
      localStorage.getItem("favoritesSortOption") ||
      "name"
  );
  const [sortDirection, setSortDirection] = useState(
    () =>
      location.state?.sortDirection ||
      localStorage.getItem("favoritesSortDirection") ||
      "asc"
  );

  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentStreamUrl, setCurrentStreamUrl] = useState(null);
  const [currentStreamTitle, setCurrentStreamTitle] = useState(null);
  const [currentPlayingItem, setCurrentPlayingItem] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const longPressRef = useRef(null);
  const longFiredRef = useRef(false);

  // Re-fetch bookmarks if we landed here without state (e.g. on refresh).
  useEffect(() => {
    if (items.length > 0) return;

    (async () => {
      try {
        const res = await apiService.webPlayerGetBookmarks();
        const raw = res?.data?.[meta.key] || [];
        const normalized = raw.map((it) => ({
          ...it,
          id: it.mediaId || it.id,
          mediaId: it.mediaId || it.id,
          name: it.name || it.title,
          poster: it.posterPath,
          type: meta.mediaType === "live-tv" ? "live-tv" : meta.mediaType,
        }));

        setItems(normalized);
      } catch (error) {
        console.error("Failed to load favorites grid:", error);
      }
    })();
  }, [items.length, meta.key, meta.mediaType]);

  // Close on Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !playerOpen) navigate(-1);
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate, playerOpen]);

  const handleSortChange = (option, direction) => {
    setSortOption(option);
    setSortDirection(direction);
    localStorage.setItem("favoritesSortOption", option);
    localStorage.setItem("favoritesSortDirection", direction);
  };

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? items.filter((it) =>
          String(it.name || it.title || "")
            .toLowerCase()
            .includes(needle)
        )
      : items;

    return sortFavorites(filtered, sortOption, sortDirection);
  }, [items, query, sortOption, sortDirection]);

  const handleItemClick = (item) => {
    if (type === "tv") {
      let { streamUrl } = item;

      if (!streamUrl && item.id && playlist?.url) {
        const baseUrl = playlist.url.replace(/\/$/, "");

        streamUrl = `${baseUrl}/${playlist.username}/${playlist.password}/${item.id}`;
      }
      if (streamUrl && !streamUrl.startsWith("http")) {
        try {
          streamUrl = atob(streamUrl);
        } catch (e) {
          console.error("Failed to decode stream URL:", e);
        }
      }
      if (streamUrl) {
        setCurrentPlayingItem(item);
        setCurrentStreamUrl(streamUrl);
        setCurrentStreamTitle(item.name || item.title);
        setPlayerOpen(true);
      }
    } else if (type === "cast") {
      const castId = item.mediaId || item.id;

      if (castId) {
        navigate(`/web-player/cast/${castId}`, {
          state: { playlist, profile },
        });
      }
    } else if (type === "collection") {
      navigate("/web-player/movies/collections", {
        state: {
          playlist,
          profile,
          collectionId: item.mediaId || item.id,
          collectionName: item.name,
        },
      });
    } else if (type === "movie") {
      navigate(`/web-player/movie/${item.id}`, {
        state: { playlist, profile, movie: item },
      });
    } else {
      navigate(`/web-player/series/${item.id}`, {
        state: { playlist, profile, series: item },
      });
    }
  };

  const logoOf = (item) =>
    item.logoPath || item.logo || item.stream_icon || item.icon || item.image;
  const posterOf = (item) => item.poster || item.posterPath || item.thumbnail;

  const handleClear = async (item) => {
    const mediaType =
      type === "tv"
        ? "live-tv"
        : type === "series"
          ? "series"
          : type === "cast"
            ? "cast"
            : type === "collection"
              ? "collection"
              : "movie";
    const id = item.mediaId || item.id;

    setItems((prev) => prev.filter((x) => (x.mediaId || x.id) !== id));
    try {
      await apiService.webPlayerDeleteBookmark(id, mediaType);
      toast.success("Removed from favorites");
    } catch (error) {
      console.error("Failed to remove:", error);
      toast.error("Failed to remove");
    }
  };

  const startPress = (item) => {
    longFiredRef.current = false;
    longPressRef.current = setTimeout(() => {
      longFiredRef.current = true;
      setContextMenu(item);
    }, 500);
  };

  const endPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  const onCardClick = (item) => {
    if (longFiredRef.current) {
      longFiredRef.current = false;

      return;
    }
    handleItemClick(item);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-950 to-black">
      <LeftMenu
        activeMenuItem="watchlist"
        playlist={playlist}
        profile={profile}
      />
      <MobileMenu />

      <div className="ml-0 lg:ml-20 px-4 sm:px-6 lg:px-8 pb-12">
        {/* Header */}
        <div className="flex flex-col gap-4 pt-10 pb-6 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            {meta.title}
            <span className="text-lg text-gray-400 ml-2 align-super">
              ({visible.length})
            </span>
          </h1>

          <div className="flex items-center gap-3">
            <div className="relative">
              <i className="fas fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400" />
              <input
                className="w-56 rounded-xl border border-white/15 bg-white/[0.06] py-2 pl-9 pr-3 text-sm text-white outline-none backdrop-blur-md placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500"
                placeholder="Search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <FavoritesSortPill
              direction={sortDirection}
              option={sortOption}
              onChange={handleSortChange}
            />
          </div>
        </div>

        {/* Grid */}
        {visible.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            {query ? "No matches" : "Nothing here yet"}
          </div>
        ) : (
          <div
            className={`grid gap-4 ${
              isLogo
                ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5"
                : "grid-cols-3 sm:grid-cols-4 md:grid-cols-5 xl:grid-cols-6"
            }`}
          >
            {visible.map((item, idx) => (
              <button
                className="group relative text-left"
                key={item.id || idx}
                type="button"
                onClick={() => onCardClick(item)}
                onMouseDown={() => startPress(item)}
                onMouseLeave={endPress}
                onMouseUp={endPress}
                onTouchEnd={endPress}
                onTouchStart={() => startPress(item)}
              >
                <i className="fas fa-star absolute right-2 top-2 z-10 text-sm text-yellow-400 drop-shadow" />
                {isLogo ? (
                  <div
                    className="lunova-card is-landscape flex aspect-video items-center justify-center card-radius-landscape p-4 group-"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(50,50,65,0.9) 100%)",
                    }}
                  >
                    <div className="lunova-card-clip">
                      {logoOf(item) ? (
                        <img
                          alt={item.name}
                          className="max-h-16 max-w-full object-contain"
                          src={logoOf(item)}
                        />
                      ) : (
                        <i className="fa-solid fa-image text-3xl text-white/40" />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="lunova-card  card-radius-poster group-">
                    <div className="lunova-card-clip">
                      {posterOf(item) ? (
                        <img
                          alt={item.name}
                          className="aspect-[2/3] w-full object-cover"
                          src={posterOf(item)}
                        />
                      ) : (
                        <div className="flex aspect-[2/3] w-full items-center justify-center bg-gray-800">
                          <i className="fas fa-film text-3xl text-gray-600" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="mt-2 truncate text-center text-sm text-white">
                  {item.name || item.title}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {contextMenu && (
        <FavoritesContextMenu
          item={contextMenu}
          type={type}
          onClose={() => setContextMenu(null)}
          onPlay={() => handleItemClick(contextMenu)}
          onRemove={() => handleClear(contextMenu)}
        />
      )}

      {playerOpen && (
        <PlyrVideoPlayer
          isOpen={playerOpen}
          mediaId={currentPlayingItem?.mediaId || currentPlayingItem?.id}
          mediaType="livetv"
          streamUrl={currentStreamUrl}
          title={currentStreamTitle}
          onClose={() => {
            setPlayerOpen(false);
            setCurrentStreamUrl(null);
            setCurrentStreamTitle(null);
            setCurrentPlayingItem(null);
          }}
        />
      )}
    </div>
  );
};

export default WebPlayerFavoritesGrid;
